import * as THREE from 'three';
import type { Military, Soldier, Patrol } from './military';
import type { Aircraft, FlightLeg } from './aviation';
import type { Vec } from '../destruction/structure';
import { militaryZone } from '../world/strategic-districts';
import { overlapsRoadBody } from './traffic-space';
import { MILITARY_BALANCE as BALANCE } from './military-balance';

interface AirTeam {
  aircraft: Aircraft;
  crew: Soldier[];
  target?: THREE.Vector3;
  dropping: boolean;
  until: number;
}
const v = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);
/** Two hundred extra original identities: 50 foot patrols, 50 in ten vehicles,
 * and two ready air teams of 50 (two pilots each). No casualty replacement. */
export class SpecialOperations {
  troops: Soldier[] = [];
  flights: AirTeam[] = [];
  sorties = 0;
  assistance = 0;
  private alert?: { point: THREE.Vector3; until: number };
  private scanAt = 0;
  constructor(readonly army: Military) {}
  clear(): void {
    for (const f of this.flights) this.army.population.aviation?.troops.cancel(f.aircraft);
    this.troops = [];
    this.flights = [];
    this.alert = undefined;
    this.sorties = this.assistance = this.scanAt = 0;
  }
  *resetSteps(): Generator<void> {
    this.clear();
    const { city, population, physics } = this.army;
    const civil = population.civil,
      aviation = population.aviation;
    if (!city.streetLife || !civil || !aviation || !city.strategic) return;
    const nodes = civil.network.nodes.filter(
      (n) =>
        n.layer === 'street' &&
        militaryZone(city, n) &&
        n.y < 5 &&
        !!physics.ground(n, 0.2, 0.3) &&
        !physics.blocked(n.x, n.z, 0.3, n.y + 0.85),
    );
    const available = nodes.filter(
      (n) => !population.people.some((p) => !p.carrier && p.p.distanceToSquared(n) < 0.7),
    );
    if (available.length < 50)
      throw new Error('Elite base patrol requires fifty supported unoccupied positions');
    const create = (index: number, point: Vec, role: string) => {
      const s = this.army.createFriendly(600 + index, point, role, true);
      s.home = civil.nodePlace(nodes[(index * 17) % nodes.length]);
      s.duty = civil.nodePlace(nodes[(index * 17 + 11) % nodes.length]);
      this.troops.push(s);
      return s;
    };
    for (let i = 0; i < 50; i++) {
      const node = available[Math.floor((i * available.length) / 50)];
      const s = create(i, node, 'special operations foot patrol');
      s.person.layer = 'street';
      s.retry = i * 0.07;
      civil.resume(s.person, 'street');
      s.person.controlled = true;
      if (i % 10 === 9) yield;
    }
    const network = population.network;
    const edges = [...network.edges.values()].filter(
      (e) =>
        militaryZone(city, network.nodes.get(e.from)!) &&
        militaryZone(city, network.nodes.get(e.to)!) &&
        network.open(e, true, 5.2),
    );
    for (let i = 0; i < 10; i++) {
      let patrol: Patrol | undefined;
      for (let j = 0; j < edges.length * 8; j++) {
        const edge = edges[(i * 7 + j) % edges.length],
          from = j % 2 ? edge.to : edge.from;
        const point = network.waypoint(edge, from, 0.12 + (Math.floor(j / edges.length) % 8) * 0.1);
        const end = network.waypoint(edge, from),
          yaw = Math.atan2(point.x - end.x, point.z - end.z);
        if (
          population.cars.some((c) =>
            overlapsRoadBody(
              { ...point, yaw, width: 2.9, length: 6.8 },
              { x: c.p.x, z: c.p.z, yaw: c.yaw, width: c.width, length: c.length },
              1,
            ),
          )
        )
          continue;
        const car = population.createCar(`military:elite:patrol:${i}`, 'humvee', point, from);
        car.controlled = true;
        car.color = 0x303335;
        car.name = `Blackguard response ${i + 1}`;
        car.speed = 12;
        car.protection = 3;
        car.state = 'enroute';
        car.yaw = yaw;
        car.q.setFromAxisAngle(v(0, 1, 0), yaw);
        car.previousQ.copy(car.q);
        car.body.setRotation(car.q, false);
        car.plan = [end];
        patrol = {
          car,
          crew: [],
          index: 40 + i,
          turns: 0,
          phase: 'patrol',
          timer: 0,
          checks: 0,
          baseOnly: true,
        };
        for (let seat = 0; seat < 5; seat++) {
          const s = create(
            50 + i * 5 + seat,
            point,
            seat === 0 ? 'elite driver' : seat === 1 ? 'elite gunner' : 'elite response',
          );
          s.state = 'aboard';
          s.vehicle = car;
          s.seat = seat;
          s.person.carrier = car.id;
          s.person.exposedSeat = seat === 1;
          patrol.crew.push(s);
        }
        this.army.patrols.push(patrol);
        break;
      }
      if (!patrol) throw new Error(`No collision-free starting space for elite patrol ${i}`);
      yield;
    }
    for (let i = 0; i < 2; i++) {
      // Ready air patrols start over the base in separate altitude lanes. The
      // original cargo floor supports every initial crew position.
      const a = aviation.createMission(
        `garrison:elite:${i}`,
        'friendly',
        v(-850, 190 + i * 55, -300),
        v(0, 0, -1),
      );
      yield;
      const team: AirTeam = { aircraft: a, crew: [], dropping: false, until: 0 };
      for (let k = 0; k < 50; k++) {
        const slot = k < 2 ? k : 50 + k;
        const s = create(
          100 + i * 50 + k,
          aviation.troops.point(a, aviation.troops.slot(slot)),
          k < 2 ? 'elite flight operator' : 'elite airborne response',
        );
        s.assignment = 'air';
        if (!aviation.troops.attach(a, s, slot)) throw new Error(`Unsupported elite cargo position ${s.id}`);
        team.crew.push(s);
        if (k % 10 === 9) yield;
      }
      a.pilots = team.crew.slice(0, 2).map((s) => s.id);
      this.flights.push(team);
      aviation.routeMission(a, this.orbit(team, i), 'Elite air team holding over its home base');
      yield;
    }
    if (this.troops.length !== BALANCE.eliteCount)
      throw new Error('Elite original roster accounting mismatch');
  }
  private orbit(team: AirTeam, index: number): FlightLeg[] {
    const p = team.target,
      height = p ? Math.max(210 + index * 55, p.y + 140) : 190 + index * 55;
    const x = p?.x ?? -845,
      z = p?.z ?? -240;
    const dx = p ? 150 : 85,
      dz = p ? 100 : 225;
    return [
      [x - dx, z - dz],
      [x + dx, z - dz],
      [x + dx, z + dz],
      [x - dx, z + dz],
      [x - dx, z - dz],
    ].map(([a, b]) => ({ point: v(a, height, b), speed: p ? 48 : 32, state: 'excursion' }));
  }
  respond(point: Vec): void {
    this.alert = { point: v(point.x, point.y, point.z), until: this.army.time + 150 };
    for (const [i, team] of this.flights.entries()) {
      if (
        team.target ||
        team.aircraft.state === 'wreck' ||
        !team.crew.slice(2).some((s) => s.person.carrier === team.aircraft.id && !s.person.fatal)
      )
        continue;
      team.target = this.alert.point.clone();
      team.until = this.alert.until;
      team.dropping = false;
      if (
        this.army.population.aviation!.routeMission(
          team.aircraft,
          this.orbit(team, i),
          'Elite original air team responding to a supported drop area',
        )
      )
        this.sorties++;
    }
  }
  standDown(): void {
    this.alert = undefined;
    for (const [i, team] of this.flights.entries()) {
      this.army.population.aviation?.troops.cancel(team.aircraft);
      team.target = undefined;
      team.dropping = false;
      this.army.population.aviation?.routeMission(
        team.aircraft,
        this.orbit(team, i),
        'Elite survivors returning to base air patrol',
      );
    }
  }
  update(dt: number): void {
    const { population, time, mobilised } = this.army;
    const aviation = population.aviation;
    if (!aviation || !this.troops.length) return;
    if (time >= this.scanAt) {
      this.scanAt = time + 2;
      const incident = [...this.army.relief.alerts.values()].find((a) => a.until > time);
      const hostile = population.invasion?.hostile.find((s) => s.state === 'deployed' && !s.person.fatal);
      if (mobilised && hostile) this.respond(hostile.person.p);
      else if (!mobilised && incident) this.respond(incident.point);
      if (this.alert && this.alert.until <= time && !mobilised) this.standDown();
    }
    for (const [i, team] of this.flights.entries()) {
      const a = team.aircraft;
      if (a.state === 'wreck') continue;
      if (!a.route.length)
        aviation.routeMission(
          a,
          this.orbit(team, i),
          team.target ? 'Elite air team completing its supported drop circuit' : 'Elite base air patrol',
        );
      if (team.target && !team.dropping && Math.hypot(a.p.x - team.target.x, a.p.z - team.target.z) < 190) {
        team.dropping = aviation.troops.beginDrop(
          a,
          new Map(
            team.crew
              .slice(2)
              .filter((s) => !s.person.fatal && s.person.carrier === a.id)
              .map((s) => [s.id, team.target!]),
          ),
        );
      }
    }
    if (this.alert && !mobilised) {
      // Airborne survivors and the foot reserve use the same real first-aid and
      // rubble-handling jobs as emergency crews, with shared claims/access.
      for (const s of this.troops) {
        if (
          s.person.carrier ||
          s.person.fatal ||
          s.transition ||
          s.vehicle ||
          ['airborne', 'incapacitated', 'lost'].includes(s.state)
        )
          continue;
        if (population.services?.assistance.step(s.person, this.alert.point, dt)) {
          s.state = 'inspection';
          this.assistance++;
        }
      }
    } else if (!mobilised) {
      for (const s of this.troops)
        if (!s.person.carrier && !s.vehicle && !s.person.fatal && s.state === 'inspection') {
          population.services?.assistance.release(s.person);
          s.state = 'base';
          s.assignment = undefined;
          s.task = '';
        }
    }
  }
  snapshot(): Record<string, number> {
    return {
      eliteRoster: this.troops.length,
      eliteAboard: this.troops.filter((s) => !!s.person.carrier).length,
      eliteAirborne: this.troops.filter((s) => s.state === 'airborne').length,
      eliteSorties: this.sorties,
      eliteAssistanceTicks: this.assistance,
    };
  }
}
